import { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import AdminLayout from '../templates/AdminLayout';
import PageHeader from '../molecules/PageHeader';
import AppSpinner from '../atoms/AppSpinner';
import PdvProductPicker from '../organisms/PdvProductPicker';
import PdvCart from '../organisms/PdvCart';
import { useToast } from '../contexts/ToastContext';
import { listCategories, listProducts } from '../api/menuApi';
import { createOrder, MODALITIES } from '../api/ordersApi';
import { getCached, setCached } from '../api/sessionCache';

const PDV_CACHE_KEY = 'pdv-catalog';

const EMPTY_FORM = {
  customerName: '',
  customerPhone: '',
  modality: 'dine_in',
  address: '',
  tableLabel: '',
  notes: '',
};

function PdvPage() {
  const toast = useToast();
  const cachedCatalog = getCached(PDV_CACHE_KEY);
  const [categories, setCategories] = useState(cachedCatalog?.categories || []);
  const [products, setProducts] = useState(cachedCatalog?.products || []);
  const [loading, setLoading] = useState(!cachedCatalog);
  const [loadError, setLoadError] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([listCategories(), listProducts()])
      .then(([cats, prods]) => {
        const available = prods.filter((p) => p.isAvailable);
        // Categoria sem produto disponível não vira aba: no balcão, uma aba
        // vazia é só um toque perdido.
        const withProducts = cats
          .filter((c) => c.isActive && available.some((p) => p.categoryId === c.id))
          .sort((a, b) => a.sortOrder - b.sortOrder);
        const sortedProducts = available.sort((a, b) => a.sortOrder - b.sortOrder);
        setCategories(withProducts);
        setProducts(sortedProducts);
        setCached(PDV_CACHE_KEY, { categories: withProducts, products: sortedProducts });
      })
      .catch((e) => setLoadError(e.message))
      .finally(() => setLoading(false));
  }, []);

  function addItem(item) {
    setItems((prev) => {
      // Mesmo produto, mesma variação, mesma metade e mesmos adicionais: soma
      // na linha existente em vez de repetir — dez toques no mesmo item não
      // viram dez linhas. Adicionais entram na comparação porque mudam o preço.
      const sameAddons = (a = [], b = []) =>
        a.length === b.length && [...a].sort().join() === [...b].sort().join();
      const twin = prev.find(
        (i) => i.productId === item.productId
          && i.variantId === item.variantId
          && i.secondProductId === item.secondProductId
          && i.secondVariantId === item.secondVariantId
          && sameAddons(i.addonIds, item.addonIds),
      );
      if (twin) {
        return prev.map((i) => (i === twin ? { ...i, quantity: i.quantity + 1 } : i));
      }
      return [...prev, item];
    });
    setError(null);
  }

  function changeQuantity(key, quantity) {
    if (quantity < 1) {
      setItems((prev) => prev.filter((i) => i.key !== key));
      return;
    }
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity } : i)));
  }

  async function handleSubmit() {
    const modality = MODALITIES.find((m) => m.value === form.modality);
    if (!items.length) {
      setError('Adicione ao menos um item.');
      return;
    }
    if (!form.customerName.trim()) {
      setError('Informe o nome do cliente.');
      return;
    }
    if (modality?.needs === 'address' && !form.address.trim()) {
      setError('Informe o endereço de entrega.');
      return;
    }
    if (modality?.needs === 'tableLabel' && !form.tableLabel.trim()) {
      setError('Informe a mesa ou comanda.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const order = await createOrder({
        customerName: form.customerName.trim(),
        customerPhone: form.customerPhone.trim() || null,
        modality: form.modality,
        address: form.address.trim() || null,
        tableLabel: form.tableLabel.trim() || null,
        notes: form.notes.trim() || null,
        items: items.map((i) => ({
          productId: i.productId,
          variantId: i.variantId,
          secondProductId: i.secondProductId,
          secondVariantId: i.secondVariantId,
          addonIds: i.addonIds || [],
          quantity: i.quantity,
        })),
      });
      toast.success(`Pedido #${order.number} enviado para a cozinha!`);
      // Limpa para o próximo atendimento, que é o que vem a seguir no balcão.
      setItems([]);
      setForm(EMPTY_FORM);
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout>
      <PageHeader title="PDV" />

      {loadError && (
        <Alert variant="danger" style={{ borderRadius: 10 }}>
          Não foi possível carregar o cardápio: {loadError}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: 13, borderRadius: 8 }}>
          {error}
        </Alert>
      )}

      {loading ? <AppSpinner /> : (
        <div className="pdv-layout">
          <div className="pdv-catalog">
            {categories.length === 0 ? (
              <p className="items-summary-empty">
                Nenhum produto disponível. Cadastre o cardápio antes de lançar pedidos.
              </p>
            ) : (
              <PdvProductPicker categories={categories} products={products} onAdd={addItem} />
            )}
          </div>

          <PdvCart
            form={form}
            onChange={setForm}
            items={items}
            onQuantity={changeQuantity}
            onRemove={(key) => setItems((prev) => prev.filter((i) => i.key !== key))}
            onSubmit={handleSubmit}
            saving={saving}
          />
        </div>
      )}
    </AdminLayout>
  );
}

export default PdvPage;
